/**
 * DNH — AuthCallback page.
 * Handles the URL fragment from Emergent OAuth:  #session_id=<token>
 * Exchanges it for a persistent session cookie, then redirects to /account.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/dnh/AuthContext";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace("#", "?"));
    const sessionId = params.get("session_id");

    if (!sessionId) {
      navigate("/signin", { replace: true });
      return;
    }

    fetch(`${BASE_URL}/api/auth/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Session exchange failed");
        return res.json();
      })
      .then(async (data) => {
        // Clear the hash so it doesn't trigger re-processing
        window.history.replaceState({}, document.title, window.location.pathname);
        const signedIn = await checkAuth({ force: true });
        if (!signedIn) throw new Error("Session could not be confirmed");
        // Redirect back to where user was before sign-in
        const from = localStorage.getItem("dnh_auth_from") || "/account";
        localStorage.removeItem("dnh_auth_from");
        navigate(from, { replace: true, state: { user: data.user } });
      })
      .catch(() => {
        navigate("/signin", { replace: true });
      });
  }, [navigate, checkAuth]);

  return (
    <div
      className="min-h-screen bg-[var(--paper)] flex items-center justify-center"
      data-testid="auth-callback"
    >
      <div className="text-center">
        <div
          className="w-6 h-6 border-2 border-[var(--hairline)] border-t-[var(--crimson)] rounded-full animate-spin mx-auto mb-4"
          aria-hidden
        />
        <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[var(--muted)]">
          Verifying your session&hellip;
        </p>
      </div>
    </div>
  );
}
